/**
 * Pure date-only calendar arithmetic. No JavaScript Date objects are used, so
 * no time-zone conversion can ever move an event to a different day.
 * Proleptic Gregorian calendar; supported range for the product is 1900–2100.
 */
export const MIN_YEAR = 1900;
export const MAX_YEAR = 2100;

export interface YMD {
  y: number;
  m: number; // 1-12
  d: number; // 1-31
}

export const isLeapYear = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const daysInMonth = (y: number, m: number): number => (m === 2 && isLeapYear(y) ? 29 : DAYS[m - 1]);

/** Day of week, 0 = Sunday … 6 = Saturday. */
export function weekday(y: number, m: number, d: number): number {
  return ((toDayNumber({ y, m, d }) % 7) + 7 + 4) % 7; // day 0 (1970-01-01) was a Thursday
}

/** Days since 1970-01-01 (H. Hinnant's days_from_civil). */
export function toDayNumber({ y, m, d }: YMD): number {
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;
  const mp = (m + 9) % 12;
  const doy = Math.floor((153 * mp + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

export function fromDayNumber(n: number): YMD {
  const z = n + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  return { y: yoe + era * 400 + (m <= 2 ? 1 : 0), m, d };
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');
export const dateKey = (y: number, m: number, d: number): string => `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
export const ymdKey = (v: YMD): string => dateKey(v.y, v.m, v.d);

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function parseDateKey(s: string): YMD | null {
  const r = DATE_RE.exec(s.trim());
  if (!r) return null;
  const y = +r[1], m = +r[2], d = +r[3];
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) return null;
  return { y, m, d };
}
export const isValidDateKey = (s: string): boolean => parseDateKey(s) !== null;

export function addDays(key: string, n: number): string {
  const v = parseDateKey(key);
  if (!v) throw new Error(`Invalid date ${key}`);
  return ymdKey(fromDayNumber(toDayNumber(v) + n));
}
export const keyToDayNumber = (key: string): number => {
  const v = parseDateKey(key);
  if (!v) throw new Error(`Invalid date ${key}`);
  return toDayNumber(v);
};

/**
 * ISO 8601 week number: weeks start on Monday and week 1 is the week that
 * contains the year's first Thursday.
 */
export function isoWeek(y: number, m: number, d: number): { year: number; week: number } {
  const dn = toDayNumber({ y, m, d });
  const isoDow = ((weekday(y, m, d) + 6) % 7) + 1; // Mon=1..Sun=7
  const thursday = fromDayNumber(dn - isoDow + 4);
  const jan1 = toDayNumber({ y: thursday.y, m: 1, d: 1 });
  return { year: thursday.y, week: Math.floor((dn - isoDow + 4 - jan1) / 7) + 1 };
}

export type WeekStart = 'monday' | 'sunday';
export type RowMode = 'fixed6' | 'actual';

export interface GridCell {
  key: string;
  y: number;
  m: number;
  d: number;
  weekday: number; // 0=Sun
  inMonth: boolean;
}
export interface MonthGrid {
  year: number;
  month: number;
  weekStart: WeekStart;
  /** Weekday index (0=Sun) for each column. */
  columns: number[];
  rows: GridCell[][];
  /** ISO week number shown for each row: the ISO week of the row's Monday. */
  weekNumbers: number[];
}

export function monthGrid(year: number, month: number, weekStart: WeekStart, rowMode: RowMode): MonthGrid {
  const startDow = weekStart === 'monday' ? 1 : 0;
  const first = weekday(year, month, 1);
  const offset = (first - startDow + 7) % 7;
  const dim = daysInMonth(year, month);
  const rowCount = rowMode === 'fixed6' ? 6 : Math.ceil((offset + dim) / 7);
  const startDn = toDayNumber({ y: year, m: month, d: 1 }) - offset;
  const rows: GridCell[][] = [];
  const weekNumbers: number[] = [];
  for (let r = 0; r < rowCount; r++) {
    const row: GridCell[] = [];
    for (let c = 0; c < 7; c++) {
      const v = fromDayNumber(startDn + r * 7 + c);
      row.push({ key: ymdKey(v), ...v, weekday: weekday(v.y, v.m, v.d), inMonth: v.m === month && v.y === year });
    }
    rows.push(row);
    const monday = row.find((c) => c.weekday === 1)!;
    weekNumbers.push(isoWeek(monday.y, monday.m, monday.d).week);
  }
  const columns = Array.from({ length: 7 }, (_, i) => (startDow + i) % 7);
  return { year, month, weekStart, columns, rows, weekNumbers };
}

/** Easter Sunday (Gregorian, anonymous/Meeus algorithm). */
export function easterSunday(year: number): YMD {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { y: year, m: month, d: day };
}

export const prevMonth = (y: number, m: number) => (m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 });
export const nextMonth = (y: number, m: number) => (m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 });
